"use client";

import { useState } from "react";
import { Plus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
export default function CommonQuestions() {
  const categories = [{
    id: "all",
    label: "ALL"
  }, {
    id: "trusts",
    label: "TRUSTS"
  }, {
    id: "protection",
    label: "PROTECTION"
  }, {
    id: "safeguards",
    label: "SAFEGUARDS"
  }, {
    id: "process",
    label: "PROCESS"
  }];
  const faqs = [{
    id: "01",
    category: "trusts",
    question: "How does a Revocable Trust avoid Florida probate?",
    answer: "By transferring ownership of your assets to a trust during your lifetime, those assets are no longer part of your individual estate at death. This allows for immediate transfer to beneficiaries without court intervention or public record.",
    spec: "INDEX/01 · PRIVACY · NON-PROBATE"
  }, {
    id: "02",
    category: "protection",
    question: "Can Florida law protect my primary residence from creditors?",
    answer: "Florida offers some of the strongest Homestead protections in the nation. Our strategies leverage these constitutional rights alongside specific trust structures to shield your primary residence from most judgment creditors.",
    spec: "INDEX/02 · HOMESTEAD · ASSET SHIELD"
  }, {
    id: "03",
    category: "safeguards",
    question: "What happens to my minor children's inheritance?",
    answer: "We implement 'Kid Trusts' within your estate plan. These ensure that assets are managed by a trusted fiduciary for your children's health, education, and support until they reach specific ages or milestones you define.",
    spec: "INDEX/03 · FIDUCIARY · MINOR PROTECTION"
  }, {
    id: "04",
    category: "process",
    question: "What is included in a 'Legacy Review' consultation?",
    answer: "A Legacy Review is a comprehensive review of your current asset structure, existing legal documents, and long-term goals. We identify vulnerabilities in your current plan and propose a structured wealth preservation roadmap.",
    spec: "INDEX/04 · AUDIT · STRATEGIC REVIEW"
  }, {
    id: "05",
    category: "trusts",
    question: "Will I lose control of my assets with a Revocable Trust?",
    answer: "No. As the Grantor and initial Trustee, you maintain full control over all assets within the trust. You can buy, sell, or move assets and even dissolve the trust entirely at any time during your life.",
    spec: "INDEX/05 · CONTROL · FLEXIBILITY"
  }, {
    id: "06",
    category: "protection",
    question: "Are business interests covered under wealth preservation?",
    answer: "Absolutely. We utilize specialized entities and multi-layered trust structures to separate personal liability from business operations, ensuring your family's wealth remains insulated from professional risks.",
    spec: "INDEX/06 · ENTITY · LIABILITY SHIELD"
  }, {
    id: "07",
    category: "safeguards",
    question: "How often should my estate plan be updated?",
    answer: "We recommend a formal review every three years or upon significant life events such as marriage, birth, or substantial changes in Florida tax law. Our firm provides ongoing monitoring for our legacy clients.",
    spec: "INDEX/07 · 3-YEAR CYCLE · MONITORING"
  }, {
    id: "08",
    category: "process",
    question: "Do you handle probate or litigation matters?",
    answer: "Legacy Wealth Legal focuses exclusively on proactive planning and wealth preservation. By specializing in the 'front-end' of estate law, we ensure your family avoids the 'back-end' costs of probate and court battles.",
    spec: "INDEX/08 · EXCLUSIVE · PRESERVATION"
  }];
  const [activeCategory, setActiveCategory] = useState("all");
  const filteredFaqs = activeCategory === "all" ? faqs : faqs.filter(f => f.category === activeCategory);
  return <section data-section-id="4978" id="common-questions" className="bg-background text-foreground py-20 md:py-[140px] px-6 lg:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="mb-16 md:mb-24">
          <div className="flex items-center gap-4 mb-6"><br /></div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-[56px] font-medium leading-[1.2] tracking-tight text-foreground">
            Common inquiries.
          </h2>
          <div className="w-2/3 h-[1px] bg-border mt-8" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <aside className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-6 pb-4 border-b border-border"><br /></div>
              <nav className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible">
                {categories.map((cat, i) => {
                const isActive = activeCategory === cat.id;
                return <Button data-index={i} key={cat.id} onClick={() => setActiveCategory(cat.id)} variant="ghost" className={`font-mono text-xs uppercase tracking-[0.1em] text-left h-auto py-3 px-4 border-l-2 rounded-md justify-start transition-all whitespace-nowrap ${isActive ? "border-l-primary text-foreground bg-card" : "border-l-transparent text-muted-foreground hover:text-foreground hover:border-l-border hover:bg-transparent"}`}>
                      <span className="opacity-50 mr-3">
                        {String(i).padStart(2, "0")}
                      </span>
                      {cat.label}
                    </Button>;
              })}
              </nav>

              <div className="mt-10 pt-6 border-t border-border hidden lg:block">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground space-y-2">
                  <div className="flex justify-between">
                    <span><br /></span>
                    <span className="text-foreground"><br /></span>
                  </div>
                  <div className="flex justify-between">
                    <span><br /></span>
                    <span className="text-primary"><br /></span>
                  </div>
                </div>
              </div>
            </div>
          </aside>

          <div className="lg:col-span-8">
            <Accordion type="single" collapsible className="w-full">
              {filteredFaqs.map((faq, i) => <AccordionItem data-index={i} key={faq.id} value={faq.id} className="border-b-0 border-t border-border first:border-t group/item">
                  <AccordionTrigger className="hover:no-underline py-6 [&>svg]:hidden group">
                    <div className="flex items-start gap-6 md:gap-10 w-full pr-4">
                      <span className="font-mono text-xs text-muted-foreground mt-1 shrink-0">
                        {faq.id}
                      </span>
                      <span className="font-default text-base md:text-lg font-medium text-foreground text-left flex-1 transition-transform duration-200 group-hover:translate-x-1 group-data-[state=open]:translate-x-1">
                        {faq.question}
                      </span>
                      <span className="shrink-0 mt-1">
                        <Plus className="size-4 text-primary transition-transform duration-300 group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-6">
                    <div className="ml-0 md:ml-[3.25rem] pl-6 md:pl-10 border-l border-primary/30 space-y-4">
                      <p className="font-default text-[15px] leading-relaxed text-muted-foreground">
                        {faq.answer}
                      </p>
                      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
                        {faq.spec}
                      </p>
                    </div>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            <div className="mt-12 border border-primary bg-card p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6 rounded-md shadow-md">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary mb-2"><br /></div>
                <p className="font-mono text-sm uppercase tracking-[0.1em] text-foreground">
                  Require specific legal counsel?
                </p>
              </div>
              <Button asChild variant="ghost" className="font-mono text-xs uppercase tracking-[0.1em] text-primary hover:text-primary hover:bg-primary/10 self-start md:self-auto px-6 h-auto py-3 border border-primary rounded-md">
                <Link to="/ContactPage">
                  SCHEDULE AUDIT
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}